import type { Colour } from '@risksmart-app/components/src/utils/colours';
import { colours } from '@risksmart-app/components/src/utils/colours';
import type { EnterpriseRiskRegisterResponseRow } from '@risksmart-app/drizzle/src/types';
import i18n from '@risksmart-app/i18n/src/i18n';
import type { GetEnterpriseRisksQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import type { Enterprise_Risk_Bool_Exp } from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  GetEnterpriseRisksDocument,
  Parent_Type_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import type { UseQueryOptions } from '@tanstack/react-query';
import { mapTrpcEnterpriseRisksToGraphQL } from 'src/hooks/queries/enterprise-risk/useGetEnterpriseRisksRegister';
import { useGetEnterpriseRiskSmartWidgetTableProps } from 'src/pages/enterprise-risk/config';
import type { EnterpriseRiskRegisterFields } from 'src/pages/enterprise-risk/types';

import { enterpriseRiskRegisterUrl } from '@/utils/urls';

import { UNRATED } from '../../gigawidget/types';
import { dateRangeFilter } from '../../gigawidget/util/filterHelpers';
import { createDataSource } from '../createDataSource';
import {
  dashboardDateRangeClickthroughFilter,
  dateRangeClickthroughFilter,
  defaultClickthroughFilter,
} from '../dataSourceHelpers';

export default createDataSource<
  EnterpriseRiskRegisterFields,
  { where?: Enterprise_Risk_Bool_Exp | undefined },
  GetEnterpriseRisksQuery
>({
  group: 'single',
  hasAccess: (_, isModuleEnabled) => isModuleEnabled('enterprise_risk'),
  parentTypes: [Parent_Type_Enum.EnterpriseRisk],
  documentNode: GetEnterpriseRisksDocument,
  trpcQuery: (trpc) => {
    const queryOptions = trpc.frontend.enterpriseRisk.register.queryOptions();

    // Use select to transform the data and handle the response properly
    const transformedQueryOptions = {
      ...queryOptions,
      select: (data: EnterpriseRiskRegisterResponseRow[]) => {
        return mapTrpcEnterpriseRisksToGraphQL(data);
      },
    };

    return transformedQueryOptions as UseQueryOptions<unknown, Error>;
  },
  useDefaultVariables: () => {
    return {};
  },
  useTablePropsHook: (data, options) => {
    return useGetEnterpriseRiskSmartWidgetTableProps(
      data?.enterprise_risk,
      options
    );
  },
  entityNamePlural: 'enterprise_risk_other',
  entityNameSingular: 'enterprise_risk_one',
  fields: 'enterpriseRisks.fields',
  dashboardFilterConfig: {
    tagsFilter: (_tags) => ({}),
    departmentsFilter: (_departments) => ({}),
    dateFilter: (_dateRange, _precision) => ({}),
    dateClickthroughFilter:
      dashboardDateRangeClickthroughFilter('CreatedAtTimestamp'),
  },
  clickThroughUrl: (filter) => enterpriseRiskRegisterUrl(filter),
  categoryGetters: [
    {
      id: 'tier',
      name: () => i18n.t('risks.columns.risk_tier'),
      categoryGetter: (item) => item.TierLabelled,
      clickthroughFilter: defaultClickthroughFilter('TierLabelled'),
    },
    {
      id: 'treatment',
      name: () => i18n.t('risks.columns.risk_treatment'),
      categoryGetter: (item) => item.TreatmentLabelled,
      clickthroughFilter: defaultClickthroughFilter('TreatmentLabelled'),
    },
    {
      id: 'residualRatingMean',
      name: () => i18n.t('enterpriseRisks.columns.residualRatingMean'),
      categoryGetter: (item) => ({
        key: item.ResidualMeanLabelled ?? UNRATED,
        label: item.ResidualMeanLabelled ?? UNRATED,
        sortKey: item.ResidualMeanLabelled ?? UNRATED,
      }),
      ratingColourKey: 'risk_controlled',
      clickthroughFilter: defaultClickthroughFilter('ResidualMeanLabelled', {
        unratedValue: UNRATED,
      }),
      categoryOverrideFunction: (category, _ratingFns, riskFormatters) => {
        const rating = riskFormatters({
          residualRating: category.data[0]?.score?.ResidualRatingMean,
        }).getResidualOption();

        return {
          title: rating?.label ?? UNRATED,
          color:
            colours[(rating?.color ?? 'light-grey') as Colour]
              ?.backgroundColor ?? rating?.color,
        };
      },
    },
    {
      id: 'residualRatingWorstCase',
      name: () => i18n.t('enterpriseRisks.columns.residualRatingWorstCase'),
      categoryGetter: (item) => ({
        key: item.ResidualWorstCaseLabelled ?? UNRATED,
        label: item.ResidualWorstCaseLabelled ?? UNRATED,
        sortKey: item.ResidualWorstCaseLabelled ?? UNRATED,
      }),
      ratingColourKey: 'risk_controlled',
      clickthroughFilter: defaultClickthroughFilter(
        'ResidualWorstCaseLabelled',
        {
          unratedValue: UNRATED,
        }
      ),
      categoryOverrideFunction: (category, _ratingFns, riskFormatters) => {
        const rating = riskFormatters({
          residualRating: category.data[0]?.score?.ResidualRatingWorstCase,
        }).getResidualOption();

        return {
          title: rating?.label ?? UNRATED,
          color:
            colours[(rating?.color ?? 'light-grey') as Colour]
              ?.backgroundColor ?? rating?.color,
        };
      },
    },
    {
      id: 'inherentRatingMean',
      name: () => i18n.t('enterpriseRisks.columns.inherentRatingMean'),
      categoryGetter: (item) => ({
        key: item.InherentMeanLabelled ?? UNRATED,
        label: item.InherentMeanLabelled ?? UNRATED,
        sortKey: item.InherentMeanLabelled ?? UNRATED,
      }),
      ratingColourKey: 'risk_uncontrolled',
      clickthroughFilter: defaultClickthroughFilter('InherentMeanLabelled', {
        unratedValue: UNRATED,
      }),
      categoryOverrideFunction: (category, _ratingFns, riskFormatters) => {
        const rating = riskFormatters({
          inherentRating: category.data[0]?.score?.InherentRatingMean,
        }).getInherentOption();

        return {
          title: rating?.label ?? UNRATED,
          color:
            colours[(rating?.color ?? 'light-grey') as Colour]
              ?.backgroundColor ?? rating?.color,
        };
      },
    },
    {
      id: 'inherentRatingWorstCase',
      name: () => i18n.t('enterpriseRisks.columns.inherentRatingWorstCase'),
      categoryGetter: (item) => ({
        key: item.InherentWorstCaseLabelled ?? UNRATED,
        label: item.InherentWorstCaseLabelled ?? UNRATED,
        sortKey: item.InherentWorstCaseLabelled ?? UNRATED,
      }),
      ratingColourKey: 'risk_uncontrolled',
      clickthroughFilter: defaultClickthroughFilter(
        'InherentWorstCaseLabelled',
        {
          unratedValue: UNRATED,
        }
      ),
      categoryOverrideFunction: (category, _ratingFns, riskFormatters) => {
        const rating = riskFormatters({
          inherentRating: category.data[0]?.score?.InherentRatingWorstCase,
        }).getInherentOption();

        return {
          title: rating?.label ?? UNRATED,
          color:
            colours[(rating?.color ?? 'light-grey') as Colour]
              ?.backgroundColor ?? rating?.color,
        };
      },
    },
    // Aggregated likelihood/impact dimensions (RSP-6195). Labelled values
    // resolve through the scoring settings (taxonomy fallback); fractional
    // medians carry the bare number as their label.
    {
      id: 'inherentLikelihoodMean',
      name: () => i18n.t('enterpriseRisks.columns.inherentLikelihoodMean'),
      categoryGetter: (item) => item.InherentLikelihoodMeanLabelled ?? UNRATED,
      ratingColourKey: 'likelihood',
      clickthroughFilter: defaultClickthroughFilter(
        'InherentLikelihoodMeanLabelled',
        { unratedValue: UNRATED }
      ),
    },
    {
      id: 'inherentImpactMean',
      name: () => i18n.t('enterpriseRisks.columns.inherentImpactMean'),
      categoryGetter: (item) => item.InherentImpactMeanLabelled ?? UNRATED,
      ratingColourKey: 'impact',
      clickthroughFilter: defaultClickthroughFilter(
        'InherentImpactMeanLabelled',
        { unratedValue: UNRATED }
      ),
    },
    {
      id: 'residualLikelihoodMean',
      name: () => i18n.t('enterpriseRisks.columns.residualLikelihoodMean'),
      categoryGetter: (item) => item.ResidualLikelihoodMeanLabelled ?? UNRATED,
      ratingColourKey: 'likelihood',
      clickthroughFilter: defaultClickthroughFilter(
        'ResidualLikelihoodMeanLabelled',
        { unratedValue: UNRATED }
      ),
    },
    {
      id: 'residualImpactMean',
      name: () => i18n.t('enterpriseRisks.columns.residualImpactMean'),
      categoryGetter: (item) => item.ResidualImpactMeanLabelled ?? UNRATED,
      ratingColourKey: 'impact',
      clickthroughFilter: defaultClickthroughFilter(
        'ResidualImpactMeanLabelled',
        { unratedValue: UNRATED }
      ),
    },
    {
      id: 'inherentLikelihoodMedian',
      name: () => i18n.t('enterpriseRisks.columns.inherentLikelihoodMedian'),
      categoryGetter: (item) =>
        item.InherentLikelihoodMedianLabelled ?? UNRATED,
      ratingColourKey: 'likelihood',
      clickthroughFilter: defaultClickthroughFilter(
        'InherentLikelihoodMedianLabelled',
        { unratedValue: UNRATED }
      ),
    },
    {
      id: 'inherentImpactMedian',
      name: () => i18n.t('enterpriseRisks.columns.inherentImpactMedian'),
      categoryGetter: (item) => item.InherentImpactMedianLabelled ?? UNRATED,
      ratingColourKey: 'impact',
      clickthroughFilter: defaultClickthroughFilter(
        'InherentImpactMedianLabelled',
        { unratedValue: UNRATED }
      ),
    },
    {
      id: 'residualLikelihoodMedian',
      name: () => i18n.t('enterpriseRisks.columns.residualLikelihoodMedian'),
      categoryGetter: (item) =>
        item.ResidualLikelihoodMedianLabelled ?? UNRATED,
      ratingColourKey: 'likelihood',
      clickthroughFilter: defaultClickthroughFilter(
        'ResidualLikelihoodMedianLabelled',
        { unratedValue: UNRATED }
      ),
    },
    {
      id: 'residualImpactMedian',
      name: () => i18n.t('enterpriseRisks.columns.residualImpactMedian'),
      categoryGetter: (item) => item.ResidualImpactMedianLabelled ?? UNRATED,
      ratingColourKey: 'impact',
      clickthroughFilter: defaultClickthroughFilter(
        'ResidualImpactMedianLabelled',
        { unratedValue: UNRATED }
      ),
    },
    {
      id: 'inherentLikelihoodWorstCase',
      name: () => i18n.t('enterpriseRisks.columns.inherentLikelihoodWorstCase'),
      categoryGetter: (item) =>
        item.InherentLikelihoodWorstCaseLabelled ?? UNRATED,
      ratingColourKey: 'likelihood',
      clickthroughFilter: defaultClickthroughFilter(
        'InherentLikelihoodWorstCaseLabelled',
        { unratedValue: UNRATED }
      ),
    },
    {
      id: 'inherentImpactWorstCase',
      name: () => i18n.t('enterpriseRisks.columns.inherentImpactWorstCase'),
      categoryGetter: (item) => item.InherentImpactWorstCaseLabelled ?? UNRATED,
      ratingColourKey: 'impact',
      clickthroughFilter: defaultClickthroughFilter(
        'InherentImpactWorstCaseLabelled',
        { unratedValue: UNRATED }
      ),
    },
    {
      id: 'residualLikelihoodWorstCase',
      name: () => i18n.t('enterpriseRisks.columns.residualLikelihoodWorstCase'),
      categoryGetter: (item) =>
        item.ResidualLikelihoodWorstCaseLabelled ?? UNRATED,
      ratingColourKey: 'likelihood',
      clickthroughFilter: defaultClickthroughFilter(
        'ResidualLikelihoodWorstCaseLabelled',
        { unratedValue: UNRATED }
      ),
    },
    {
      id: 'residualImpactWorstCase',
      name: () => i18n.t('enterpriseRisks.columns.residualImpactWorstCase'),
      categoryGetter: (item) => item.ResidualImpactWorstCaseLabelled ?? UNRATED,
      ratingColourKey: 'impact',
      clickthroughFilter: defaultClickthroughFilter(
        'ResidualImpactWorstCaseLabelled',
        { unratedValue: UNRATED }
      ),
    },
    {
      id: 'createdDate',
      name: () => i18n.t('columns.created_on'),
      categoryGetter: (data) => new Date(data.CreatedAtTimestamp),
      date: true,
      dashboardDateFilterOverride: (dateRange, precision) => ({
        where: { CreatedAtTimestamp: dateRangeFilter(dateRange, precision) },
      }),
      clickthroughFilter: dateRangeClickthroughFilter('CreatedAtTimestamp'),
    },
  ],
});
